import { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { toFlagId, useFlagService } from '@atlassian/jira-flags';
import { EcosystemAPIPrefillingUFOExperience } from '@atlassian/jira-issue-create-analytics/src/services/experiences/index.tsx';
import messages from './messages.tsx';

const ECOSYSTEM_API_PREFILL_DEFAULT_VALUES_API = '/rest/internal/latest/ecosystem/issue/fields';
const ECOSYSTEM_API_PREFILL_CONTEXT_FIELDS_API = '/rest/internal/latest/ecosystem/issue/context';

const ERROR_MESSAGE_ID = toFlagId('IssueCreatePrefillFailed');

type FieldsApiInputType = {
	fieldsInput: { fieldId: string; values: string[] }[];
};

type ContextFieldsApiInputType = {
	fields?: FieldsApiInputType;
	projectId?: string;
	issueTypeId?: string;
};

const isObject = (value: any): boolean => {
	return value !== null && typeof value === 'object' && !Array.isArray(value);
};

export const transformPrefilledFieldsData = (prefilledFieldsData: any): FieldsApiInputType => {
	const modifiedPrefilledFieldsData: FieldsApiInputType = {
		fieldsInput: [],
	};
	if (isObject(prefilledFieldsData)) {
		Object.keys(prefilledFieldsData).forEach((key) => {
			const data = prefilledFieldsData[key];
			if (Array.isArray(data)) {
				const modifiedData = [];
				for (const value of data) {
					if (typeof value === 'number' || typeof value === 'string')
						modifiedData.push(value.toString());
				}
				modifiedPrefilledFieldsData.fieldsInput.push({ fieldId: key, values: modifiedData });
			} else if (typeof data === 'number' || typeof data === 'string')
				modifiedPrefilledFieldsData.fieldsInput.push({ fieldId: key, values: [data.toString()] });
		});
	}
	return modifiedPrefilledFieldsData;
};

export const transformInputToFieldsAPIInputType = (
	modifiedPrefilledFieldsData: any,
): {
	headers: {
		'Content-Type': string;
		Accept: string;
	};
	body: string;
} => {
	const options = {
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(modifiedPrefilledFieldsData),
	};
	return options;
};

export const transformInputToContextFieldsAPIInputType = (
	modifiedPrefilledFieldsData: any,
	prefilledFieldsData: any,
): {
	headers: {
		'Content-Type': string;
		Accept: string;
	};
	body: string;
} => {
	const issueTypeProvided = transformInputValueToNumber(prefilledFieldsData?.issuetype)?.toString();
	const projectIdProvided = transformInputValueToNumber(prefilledFieldsData?.pid)?.toString();
	const requestData: ContextFieldsApiInputType = {
		fields: modifiedPrefilledFieldsData,
		projectId: projectIdProvided,
		issueTypeId: issueTypeProvided,
	};
	const options = {
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(requestData),
	};
	return options;
};

const transformInputValueToNumber = (value: any) => {
	let transformedValue = value;
	if (Array.isArray(value)) transformedValue = value[0];
	if (
		(typeof transformedValue === 'string' || typeof transformedValue === 'number') &&
		Number(transformedValue)
	)
		return Number(transformedValue);

	return null;
};

export const usePrefilledDefaultValues = (): (({
	prefilledFieldsData,
	callContextAPI,
}: {
	prefilledFieldsData: any;
	callContextAPI?: boolean;
}) => Promise<{
	defaultValues: any;
	issueType: any;
	projectId: any;
	requestType: any;
}>) => {
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();

	const getDefaultValues = useCallback(
		({
			prefilledFieldsData,
			callContextAPI = false,
		}: {
			prefilledFieldsData: any;
			callContextAPI?: boolean;
		}) => {
			const modifiedPrefilledFieldsData = transformPrefilledFieldsData(prefilledFieldsData);

			const fetchContextFieldsData = async () => {
				try {
					const inputOptions = transformInputToContextFieldsAPIInputType(
						modifiedPrefilledFieldsData,
						prefilledFieldsData,
					);
					const contextFields = await performPostRequest(
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						`${window.location.origin}${ECOSYSTEM_API_PREFILL_CONTEXT_FIELDS_API}`,
						inputOptions,
					);
					return contextFields;
				} catch (error: any) {
					EcosystemAPIPrefillingUFOExperience.failure();
					fireErrorAnalytics({
						meta: {
							id: 'fetchEcosystemContextFieldsData',
							packageName: 'jiraNewIssueCreateTrigger',
							teamName: 'gryffindor',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					showFlag({
						key: ERROR_MESSAGE_ID,
						type: 'error',
						title: formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.errorMessageTitleNonFinalIssueTerminologyRefreshM4
								: messages.errorMessageTitleNonFinal,
						),
						description: formatMessage(messages.errorMessageDescriptionNonFinal),
						messageId:
							'new-issue-create-trigger.use-prefilled-default-values.show-flag.error.fetch-context-fields-data.show-flag.error',
						messageType: 'transactional',
					});
				}
			};

			const fetchPrefilledDefaultValues = async () => {
				const inputOptions = transformInputToFieldsAPIInputType(modifiedPrefilledFieldsData);
				try {
					const defaultValues = await performPostRequest(
						ECOSYSTEM_API_PREFILL_DEFAULT_VALUES_API,
						inputOptions,
					);
					return defaultValues;
				} catch (error: any) {
					EcosystemAPIPrefillingUFOExperience.failure();
					fireErrorAnalytics({
						meta: {
							id: 'ecosystemAPIPrefillDefaultValues',
							packageName: 'jiraNewIssueCreateTrigger',
							teamName: 'gryffindor',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					showFlag({
						key: ERROR_MESSAGE_ID,
						type: 'error',
						title: formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.errorMessageTitleNonFinalIssueTerminologyRefreshM4
								: messages.errorMessageTitleNonFinal,
						),
						description: formatMessage(messages.errorMessageDescriptionNonFinal),
						messageId:
							'new-issue-create-trigger.use-prefilled-default-values.show-flag.error.fetch-prefilled-default-values.show-flag.error',
						messageType: 'transactional',
					});
				}
			};

			const getPrefilledData = async () => {
				if (!callContextAPI) {
					const defaultValues = await fetchPrefilledDefaultValues();
					const issueType = transformInputValueToNumber(prefilledFieldsData?.issuetype);
					const projectId = transformInputValueToNumber(prefilledFieldsData?.pid);
					const requestType = prefilledFieldsData?.requestType;
					return { defaultValues, issueType, projectId, requestType };
				}
				const [defaultValues, contextFieldsData] = await Promise.all([
					fetchPrefilledDefaultValues(),
					fetchContextFieldsData(),
				]);
				const issueType = contextFieldsData?.issueType?.issueTypeId;
				const projectId = contextFieldsData?.project?.projectId;
				const requestType = contextFieldsData?.requestType?.key;
				return { defaultValues, issueType, projectId, requestType };
			};
			return getPrefilledData();
		},
		[formatMessage, showFlag],
	);
	return getDefaultValues;
};
